import { Link } from 'react-router-dom';
import {
    RESOURCE_NAMES,
    TEAM_PLANET_BUILDING_QUEUE_LIMIT,
    TEAM_PLANET_SCIENCE_QUEUE_LIMIT,
    type AllianceRole,
} from '@kaeliux/shared';
import { useTeamPlanet } from '../hooks/useTeamPlanet';
import { formatFullNumber } from '../utils/formatters';
import { pendingBuilds } from '../utils/buildQueue';
import TeamPlanetEmptyState from './teamPlanet/TeamPlanetEmptyState';
import { Button } from './ui/Button';
import { Icon } from './ui/Icon';
import { Panel } from './ui/Panel';
import { linkButtonClasses } from './ui/buttonStyles';

function errorMessage(error: unknown, fallback: string): string {
    return error instanceof Error && error.message ? error.message : fallback;
}

function planetImage(planetType: number): string {
    return new URL(`../assets/images/planets/planet_${planetType}.png`, import.meta.url).href;
}

interface Props {
    /** The viewer's role in THEIR OWN alliance — `null` when previewing someone else's, which this tab never renders for. */
    viewerRole: AllianceRole | null;
}

/**
 * The Alliance page's window onto the shared Team planet: where it is, what
 * it holds and how busy its queues are, with a way in. Managing it happens
 * on the Team planet's own screens (`/team-planet`), reached from here or
 * from the planet switcher. Founding one happens from the PLANET PAGE of an
 * eligible planet, since a conversion picks one of the leader's own planets.
 */
export default function AllianceTeamPlanetTab({ viewerRole }: Props) {
    const teamPlanetQuery = useTeamPlanet({ refetchInterval: 15_000 });
    const teamPlanet = teamPlanetQuery.data ?? null;
    const isLeader = viewerRole === 'leader';

    if (teamPlanetQuery.isPending) {
        return <Panel variant="solid" padding="md"><p className="text-content-muted animate-pulse">Loading Team planet…</p></Panel>;
    }
    if (teamPlanetQuery.isError) {
        return <Panel variant="solid" padding="md"><p className="text-status-danger">{errorMessage(teamPlanetQuery.error, 'Failed to load the Team planet.')}</p></Panel>;
    }
    if (!teamPlanet) {
        return <TeamPlanetEmptyState reason="no-team-planet" isLeader={isLeader} showAllianceLink={false} />;
    }

    const events = (teamPlanet.tickCheckpoints ?? []).filter((e) => !e.cancelled);
    const research = events.filter((e) => e.eventType === 'scienceResearch').length;
    const shipBatches = events.filter((e) => e.eventType === 'shipBuild').length;
    const builds = pendingBuilds(teamPlanet).length;
    const { coordinates } = teamPlanet;

    return (
        <Panel variant="solid" padding="md" className="flex flex-col gap-4" data-testid="alliance-team-planet-summary">
            <div className="flex flex-wrap items-center gap-4">
                <img
                    src={planetImage(teamPlanet.planetType)}
                    alt=""
                    className="h-16 w-16 shrink-0 rounded-full border border-hairline-strong object-cover"
                />
                <div className="min-w-0 flex-1">
                    <h3 className="text-title text-content-primary">{teamPlanet.name}</h3>
                    <p className="text-body text-content-muted font-mono" data-testid="team-planet-coordinates">
                        {coordinates.galaxy}:{coordinates.system}:{coordinates.position}
                    </p>
                </div>
                <div className="flex flex-wrap items-center gap-2">
                    <Link
                        to="/team-planet"
                        className={linkButtonClasses('primary', 'md')}
                        data-testid="team-planet-open-link"
                    >
                        <Icon name="planet" size={16} aria-hidden="true" />
                        Open Team Planet
                    </Link>
                    {isLeader ? (
                        <Link to="/team-planet/fleets" className={linkButtonClasses('secondary', 'md')} data-testid="team-planet-send-fleet-link">
                            Send Fleet
                        </Link>
                    ) : (
                        <Button type="button" variant="secondary" disabled data-testid="team-planet-send-fleet-disabled">
                            Send Fleet
                        </Button>
                    )}
                </div>
            </div>

            <dl className="grid grid-cols-2 gap-x-4 gap-y-3 border-t border-hairline pt-3 sm:grid-cols-3 lg:grid-cols-6">
                {[
                    { label: RESOURCE_NAMES.metal, testId: 'team-planet-metal', value: formatFullNumber(Math.floor(teamPlanet.resources.metal.current)) },
                    { label: RESOURCE_NAMES.minerals, testId: 'team-planet-minerals', value: formatFullNumber(Math.floor(teamPlanet.resources.minerals.current)) },
                    { label: RESOURCE_NAMES.gas, testId: 'team-planet-gas', value: formatFullNumber(Math.floor(teamPlanet.resources.gas.current)) },
                    { label: 'Buildings queued', testId: 'team-planet-build-count', value: `${builds} / ${TEAM_PLANET_BUILDING_QUEUE_LIMIT}` },
                    { label: 'Research queued', testId: 'team-planet-research-count', value: `${research} / ${TEAM_PLANET_SCIENCE_QUEUE_LIMIT}` },
                    { label: 'Ship batches', testId: 'team-planet-ship-count', value: String(shipBatches) },
                ].map(({ label, testId, value }) => (
                    <div key={label} className="flex flex-col gap-0.5">
                        <dt className="text-micro text-content-muted">{label}</dt>
                        <dd className="text-body font-semibold text-content-primary tabular-nums" data-testid={testId}>{value}</dd>
                    </div>
                ))}
            </dl>

            {!isLeader && (
                <p className="text-micro text-content-muted">Only the alliance leader can dispatch fleets from the Team planet.</p>
            )}
        </Panel>
    );
}
